import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Flex, type TableColumnsType } from 'antd-v5';
import { debounce } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Search } from 'scouting-ui/lib/Input';

import type { SubUnitType } from '@appTypes/esb';
import {
  NO_SUB_UNIT_KEY,
  type RosterGroup,
  buildGroups,
} from '@modules/roster-v2/models/groupedRoster';
import {
  type RosterMemberView,
  type SubUnitRef,
  matchesSearch,
  subUnitKey,
} from '@modules/roster-v2/models/rosterMemberView';
import GroupedTable from '@shared/components/GroupedTable/GroupedTable';
import type { GroupedSummary } from '@shared/components/GroupedTable/groupedTableModel';
import type {
  GroupRowContext,
  GroupingConfig,
} from '@shared/components/GroupedTable/useGroupedTable';
import { intl } from '@shared/localization';

import GroupHeader, { type SubUnitActions } from './GroupHeader';
import styles from './NewRosterTable.module.scss';
import { SUB_UNIT_COLUMN } from './columns';

/** App header, toolbar and gaps above the table */
const DEFAULT_STICKY_OFFSET = 60 + 48 + 8 * 2;

const SEARCH_DEBOUNCE_MS = 300;

const SCROLL = { x: 'max-content' } as const;

export interface RosterTableViewProps {
  members: RosterMemberView[];
  loading?: boolean;
  /** From `getColumns`; keep it stable (`useMemo`) */
  columns: TableColumnsType<RosterMemberView>;
  /** No grouping without it */
  subUnitType?: SubUnitType;
  grouped: boolean;
  /** The groups, in order, empty ones included; keep it stable (`useMemo`) */
  subUnitRefs: readonly SubUnitRef[];
  /** Rank image URL per den level (`denTypesNames` value) */
  denLevelImages?: Partial<Record<string, string>>;
  /** Left out for read-only */
  groupActions?: SubUnitActions;
  toolbarExtra?: ReactNode;
  stickyOffset?: number;
}

/** Search, then the shared grouped table, paging people (headers repeat). */
export default function RosterTableView(props: RosterTableViewProps) {
  const {
    members,
    loading,
    columns,
    subUnitType,
    grouped,
    subUnitRefs,
    denLevelImages,
    groupActions,
    toolbarExtra,
    stickyOffset = DEFAULT_STICKY_OFFSET,
  } = props;

  const [search, setSearch] = useState('');
  const onSearch = useMemo(() => debounce(setSearch, SEARCH_DEBOUNCE_MS), []);
  useEffect(() => () => onSearch.cancel(), [onSearch]);
  const submitSearch = useCallback(
    (value: string) => {
      onSearch.cancel();
      setSearch(value);
    },
    [onSearch],
  );

  const renderGroupRow = useCallback(
    (row: GroupRowContext<RosterGroup>) => {
      if (!subUnitType) return null;
      const denType = row.group?.ref?.denType;
      return (
        <GroupHeader
          row={row}
          subUnitType={subUnitType}
          imageUrl={
            denType ? denLevelImages?.[denType.toLowerCase()] : undefined
          }
          actions={groupActions}
        />
      );
    },
    [subUnitType, denLevelImages, groupActions],
  );
  // From the unit's sub-units, so groups don't change on search
  const groups = useMemo(() => buildGroups(subUnitRefs), [subUnitRefs]);
  const getGroupKeys = useCallback(
    (member: RosterMemberView) =>
      member.subUnits.filter(ref => ref.type === subUnitType).map(subUnitKey),
    [subUnitType],
  );
  const grouping = useMemo<
    GroupingConfig<RosterMemberView, RosterGroup> | undefined
  >(
    () =>
      grouped && subUnitType
        ? {
            groups,
            getGroupKeys,
            fallbackKey: NO_SUB_UNIT_KEY,
            columnKey: SUB_UNIT_COLUMN,
            renderGroupRow,
          }
        : undefined,
    [grouped, subUnitType, groups, getGroupKeys, renderGroupRow],
  );

  const showTotal = useCallback(
    (summary: GroupedSummary) =>
      grouping && subUnitType ? (
        <FormattedMessage
          id={`rosterV2.table.total.grouped.${subUnitType}`}
          values={{ groups: summary.groups, people: summary.items }}
        />
      ) : (
        <FormattedMessage
          id="rosterV2.table.total.flat"
          values={{ people: summary.items }}
        />
      ),
    [grouping, subUnitType],
  );

  return (
    <Flex vertical gap={0}>
      <div className={styles.searchContainer}>
        <Flex align="center" gap="small" className={styles.searchRow}>
          <div className={styles.searchField}>
            <Search
              placeholder={intl.formatMessage({
                id: 'rosterV2.table.searchPlaceholder',
              })}
              allowClear
              onChange={e => onSearch(e.target.value)}
              onSearch={submitSearch}
            />
          </div>
          {toolbarExtra}
        </Flex>
      </div>
      <GroupedTable<RosterMemberView, RosterGroup>
        size="small"
        rowKey="personGuid"
        columns={columns}
        dataSource={members}
        loading={loading}
        sticky={{ offsetHeader: stickyOffset }}
        scroll={SCROLL}
        search={search}
        globalFilterFn={matchesSearch}
        grouping={grouping}
        groupPaging="people"
        showTotal={showTotal}
      />
    </Flex>
  );
}
